import { ArrowLeft, Calendar, Clock, User, Cpu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Link } from "react-router-dom";
import Footer from "@/components/Footer";
import BlogPostImage from "@/components/BlogPostImage";

const BlogPost2 = () => {
  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-64 h-64 bg-primary/20 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent/20 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }} />
      </div>

      {/* Vertical lines pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,hsl(var(--border)/0.3)_1px,transparent_1px)] bg-[size:8rem_100%] pointer-events-none" style={{ maskImage: 'radial-gradient(ellipse 80% 50% at 50% 0%, black 70%, transparent 110%), linear-gradient(to bottom, black 0%, black 85%, transparent 100%)', maskComposite: 'intersect', WebkitMaskImage: 'radial-gradient(ellipse 80% 50% at 50% 0%, black 70%, transparent 110%), linear-gradient(to bottom, black 0%, black 85%, transparent 100%)', WebkitMaskComposite: 'source-in' }} />

      {/* Header */}
      <header className="border-b border-border/50 bg-card/30 backdrop-blur-xl sticky top-0 z-50 relative">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/">
            <img src="/logo.png" alt="Coddar Logo" className="h-11 w-auto" />
          </Link>
          <nav className="hidden md:flex gap-6">
            <Link to="/#servicos" className="text-muted-foreground hover:text-primary transition-colors">
              Serviços
            </Link>
            <Link to="/blog" className="text-primary transition-colors">
              Blog
            </Link>
            <Link to="/seguranca-digital" className="text-muted-foreground hover:text-primary transition-colors">
              Segurança Digital
            </Link>
            <Link to="/contato" className="text-muted-foreground hover:text-primary transition-colors">
              Contato
            </Link>
          </nav>
          <Button asChild variant="outline" className="border-primary/50 text-primary hover:bg-primary/10 hover:border-primary">
            <a href="https://folio.app.br/area-cliente">Área do Cliente</a>
          </Button>
        </div>
      </header>

      {/* Article */}
      <article className="py-12 md:py-20 px-4 relative">
        <div className="container mx-auto max-w-4xl relative z-10">
          <Button asChild variant="ghost" className="mb-8 gap-2 hover:gap-3 transition-all">
            <Link to="/blog">
              <ArrowLeft className="h-4 w-4" /> Voltar ao Blog
            </Link>
          </Button>

          <Card className="border-border/50 bg-card/50 backdrop-blur-sm p-8 md:p-12">
            {/* Featured Image */}
            <div className="aspect-video w-full rounded-lg mb-8 overflow-hidden">
              <BlogPostImage
                title="Como a Automação Pode Transformar Seu Negócio"
                backgroundImage="/assets/blog-automacao-negocio.webp"
                icon={<Cpu className="w-full h-full" />}
              />
            </div>

            {/* Meta Info */}
            <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mb-6">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span>12 de Novembro, 2025</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                <span>10 min de leitura</span>
              </div>
              <div className="flex items-center gap-2">
                <User className="h-4 w-4" />
                <span>Equipe Coddar</span>
              </div>
            </div>

            {/* Title */}
            <h1 className="text-3xl md:text-5xl font-bold text-foreground mb-6">
              Como a Automação Pode Transformar Seu Negócio
            </h1>

            {/* Content */}
            <div className="prose prose-invert max-w-none">
              <p className="text-lg text-muted-foreground mb-6">
                Em um mundo cada vez mais competitivo, a automação de processos deixou de ser um diferencial para se tornar uma necessidade. Empresas que adotam automação conseguem reduzir custos, aumentar a produtividade e focar no que realmente importa: crescimento e inovação.
              </p>

              <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">O Que é Automação de Processos?</h2>
              <p className="text-muted-foreground mb-4">
                Automação de processos é o uso de tecnologia para executar tarefas repetitivas e baseadas em regras com mínima intervenção humana. Isso inclui desde simples scripts até sistemas complexos de RPA (Robotic Process Automation) e inteligência artificial.
              </p>

              <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Benefícios Comprovados da Automação</h2>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">1. Redução de Custos Operacionais</h3>
              <p className="text-muted-foreground mb-4">
                Estudos mostram que empresas podem reduzir custos operacionais em até 60% com automação adequada:
              </p>
              <ul className="list-disc list-inside text-muted-foreground mb-6 space-y-2">
                <li>Menos erros humanos = menos retrabalho</li>
                <li>Processos mais rápidos = maior throughput</li>
                <li>Redução de horas extras e trabalho manual</li>
                <li>Otimização de recursos e infraestrutura</li>
              </ul>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">2. Aumento de Produtividade</h3>
              <p className="text-muted-foreground mb-4">
                Colaboradores liberados de tarefas repetitivas podem focar em atividades estratégicas:
              </p>
              <ul className="list-disc list-inside text-muted-foreground mb-6 space-y-2">
                <li>Mais tempo para inovação e criatividade</li>
                <li>Foco em relacionamento com clientes</li>
                <li>Desenvolvimento de novas estratégias</li>
                <li>Análise de dados e tomada de decisões</li>
              </ul>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">3. Qualidade e Consistência</h3>
              <p className="text-muted-foreground mb-4">
                Processos automatizados garantem:
              </p>
              <ul className="list-disc list-inside text-muted-foreground mb-6 space-y-2">
                <li>Execução padronizada e sem variações</li>
                <li>Redução de erros em até 90%</li>
                <li>Conformidade com regulamentações</li>
                <li>Rastreabilidade completa de operações</li>
              </ul>

              <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Áreas Que Mais Se Beneficiam</h2>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">Financeiro e Contabilidade</h3>
              <ul className="list-disc list-inside text-muted-foreground mb-6 space-y-2">
                <li>Conciliação bancária automática</li>
                <li>Processamento de notas fiscais</li>
                <li>Geração de relatórios financeiros</li>
                <li>Controle de contas a pagar e receber</li>
              </ul>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">Recursos Humanos</h3>
              <ul className="list-disc list-inside text-muted-foreground mb-6 space-y-2">
                <li>Onboarding de novos funcionários</li>
                <li>Processamento de folha de pagamento</li>
                <li>Gestão de benefícios e férias</li>
                <li>Triagem de currículos e recrutamento</li>
              </ul>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">Atendimento ao Cliente</h3>
              <ul className="list-disc list-inside text-muted-foreground mb-6 space-y-2">
                <li>Chatbots inteligentes 24/7</li>
                <li>Respostas automáticas a perguntas frequentes</li>
                <li>Roteamento inteligente de tickets</li>
                <li>Follow-up automático de solicitações</li>
              </ul>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">Vendas e Marketing</h3>
              <ul className="list-disc list-inside text-muted-foreground mb-6 space-y-2">
                <li>Nutrição de leads automatizada</li>
                <li>Envio de e-mails personalizados em escala</li>
                <li>Qualificação automática de prospects</li>
                <li>Geração de propostas comerciais</li>
              </ul>

              <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Tecnologias de Automação</h2>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">RPA (Robotic Process Automation)</h3>
              <p className="text-muted-foreground mb-4">
                Robôs de software que imitam ações humanas em sistemas digitais. Ideal para processos estruturados e repetitivos.
              </p>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">Workflows Inteligentes</h3>
              <p className="text-muted-foreground mb-4">
                Plataformas como Zapier, Make (Integromat) e n8n permitem conectar diferentes aplicações e criar fluxos automatizados sem código.
              </p>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">IA e Machine Learning</h3>
              <p className="text-muted-foreground mb-4">
                Automação inteligente que aprende e se adapta, capaz de lidar com processos não estruturados e tomar decisões complexas.
              </p>

              <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Como Começar a Automatizar</h2>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">1. Mapeie Seus Processos</h3>
              <p className="text-muted-foreground mb-4">
                Identifique tarefas repetitivas, demoradas e propensas a erros. Documente cada etapa do processo atual.
              </p>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">2. Priorize por Impacto</h3>
              <p className="text-muted-foreground mb-4">
                Comece com processos que trazem maior retorno: alto volume, alto custo ou alto risco de erro.
              </p>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">3. Escolha a Tecnologia Adequada</h3>
              <p className="text-muted-foreground mb-4">
                Nem todo processo precisa de IA. Às vezes, um simples script resolve. Avalie complexidade vs. benefício.
              </p>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">4. Implemente Gradualmente</h3>
              <p className="text-muted-foreground mb-4">
                Comece com um piloto, aprenda, ajuste e expanda. Automação é uma jornada, não um projeto único.
              </p>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">5. Monitore e Otimize</h3>
              <p className="text-muted-foreground mb-4">
                Acompanhe métricas, colete feedback e melhore continuamente. Automação requer manutenção e evolução.
              </p>

              <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Casos de Sucesso</h2>
              <p className="text-muted-foreground mb-4">
                Nossos clientes têm alcançado resultados impressionantes:
              </p>
              <ul className="list-disc list-inside text-muted-foreground mb-6 space-y-2">
                <li>E-commerce: Redução de 80% no tempo de processamento de pedidos</li>
                <li>Escritório contábil: 70% menos tempo em conciliações bancárias</li>
                <li>Indústria: 90% de redução em erros de inventário</li>
                <li>Serviços: 5x mais leads qualificados com mesmo time de vendas</li>
              </ul>

              <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Conclusão</h2>
              <p className="text-muted-foreground mb-4">
                A automação não é sobre substituir pessoas, mas sobre potencializar o que elas fazem de melhor. Ao eliminar tarefas repetitivas e demoradas, sua equipe pode focar em criatividade, estratégia e relacionamento - áreas onde humanos são insubstituíveis.
              </p>
              <p className="text-muted-foreground">
                O momento de automatizar é agora. Empresas que adiam essa transformação perdem competitividade a cada dia. Na Coddar, temos a expertise e as ferramentas para guiar sua empresa nessa jornada de transformação digital.
              </p>
            </div>

            {/* CTA */}
            <div className="mt-12 p-6 bg-primary/10 border border-primary/20 rounded-lg">
              <h3 className="text-xl font-bold text-foreground mb-2">Pronto para automatizar seus processos?</h3>
              <p className="text-muted-foreground mb-4">
                Agende uma consulta gratuita e descubra como podemos ajudar sua empresa a economizar tempo e dinheiro com automação inteligente.
              </p>
              <Button asChild>
                <Link to="/contato">Agendar Consulta</Link>
              </Button>
            </div>
          </Card>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default BlogPost2;
